// Regla única: un cliente sin nivel SureKeep (NOT_ELIGIBLE) no ve nada de SureKeep en el wizard.
// Puro, sin React.
import type { SaleSummary } from './buildSale';
import type {
    CatalogItem,
    CatalogResponse,
    FinancierIneligibleReason,
    FinancierOption,
    WizardSession,
} from './types';

const SUREKEEP = 'SUREKEEP';

const INELIGIBLE_COPY: Partial<Record<string, string>> = {
    ACTIVE_CREDIT: 'Ya tiene un crédito activo',
} satisfies Record<FinancierIneligibleReason, string>;

const INELIGIBLE_FALLBACK_COPY = 'SureKeep no está disponible para esta venta';

/** Motivo para el vendedor cuando el servidor marca SureKeep no elegible; `undefined` si se puede elegir. */
export function sureKeepIneligibleCopy(
    option: Pick<FinancierOption, 'eligible' | 'ineligibleReason'> | undefined,
): string | undefined {
    if (option?.eligible !== false) return undefined;
    const reason = option.ineligibleReason;
    return (reason === undefined ? undefined : INELIGIBLE_COPY[reason]) ?? INELIGIBLE_FALLBACK_COPY;
}

/** `false` solo con el score resuelto en NOT_ELIGIBLE; sin score todavía SureKeep sigue en juego. */
export function isSureKeepAvailable(session: Pick<WizardSession, 'loan'> | undefined): boolean {
    return session?.loan?.level !== 'NOT_ELIGIBLE';
}

/** SureKeep confirmado: el score ya dio un nivel. Sin score todavía, la venta no pinta datos de SureKeep. */
export function isSureKeepConfirmed(session: Pick<WizardSession, 'loan'> | undefined): boolean {
    // El silo puede mandar `level: null`: solo cuenta un nivel con crédito.
    const level: unknown = session?.loan?.level;
    return level === 'BRONZE' || level === 'SILVER' || level === 'GOLD';
}

/** El equipo sin SureKeep: fuera de sus financieras y sin marcas, enganche ni tope de crédito. */
export function withoutSureKeep(item: CatalogItem): CatalogItem {
    const copy: CatalogItem = {
        ...item,
        eligibleFinanciers: item.eligibleFinanciers.filter((code) => code !== SUREKEEP),
    };
    delete copy.financeable;
    delete copy.affordableOnCredit;
    delete copy.minDownPaymentCents;
    delete copy.minDownPaymentPct;
    delete copy.creditCapCents;
    return copy;
}

/** Catálogo como lo ve la sesión: sin SureKeep, sin promos de crédito ni cotas del nivel. */
export function catalogForSession(
    catalog: CatalogResponse | undefined,
    available: boolean,
): CatalogResponse | undefined {
    if (catalog === undefined || available) return catalog;
    const copy: CatalogResponse = {
        ...catalog,
        items: catalog.items.map(withoutSureKeep),
        scopeWidePromotions: [],
    };
    delete copy.creditCapCents;
    delete copy.minDownPaymentPct;
    return copy;
}

/** Resumen de «Accesorios» sin la barra de extras a crédito. */
export function summaryForSession(summary: SaleSummary, available: boolean): SaleSummary {
    if (available || summary.credit === undefined) return summary;
    const copy = { ...summary };
    delete copy.credit;
    return copy;
}
